import { FC, useRef, useState, useEffect } from 'react'

import { NavLink, useNavigate } from 'react-router-dom'

import {
  Avatar,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Divider,
  Hidden,
  lighten,
  List,
  ListItem,
  ListItemText,
  Popover,
  Typography,
} from '@mui/material'

import { styled } from '@mui/material/styles'
import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone'
import AccountBoxTwoToneIcon from '@mui/icons-material/AccountBoxTwoTone'
import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone'
import AccountTreeTwoToneIcon from '@mui/icons-material/AccountTreeTwoTone'
import { removeToken, removeSelectedStoreId } from '../../../../utils'
import { logout , UserLoginData } from '../../../../services'
import { useAppDispatch } from '../../../../hooks'

import ApplicationLinks from '../../../../ApplicationLinks'
import AnalyticsManager from '../../../../analytics/AnalyticsManager'
const UserBoxButton = styled(Button)(
  ({ theme }) => `
        padding-left: ${theme.spacing(1)};
        padding-right: ${theme.spacing(1)};
`
)

const MenuUserBox = styled(Box)(
  ({ theme }) => `
        background: ${theme.colors.alpha.black[5]};
        padding: ${theme.spacing(2)};
`
)

const UserBoxText = styled(Box)(
  ({ theme }) => `
        text-align: left;
        padding-left: ${theme.spacing(1)};
`
)

const UserBoxLabel = styled(Typography)(
  ({ theme }) => `
        font-weight: ${theme.typography.fontWeightBold};
        color: ${theme.palette.secondary.main};
        display: block;
`
)

const UserBoxDescription = styled(Typography)(
  ({ theme }) => `
        color: ${lighten(theme.palette.secondary.main, 0.5)}
`
)

type HeaderUserboxProps = {
  data: UserLoginData
}

const HeaderUserbox: FC<HeaderUserboxProps> = ({ data }) => {
  const dispatchAction = useAppDispatch()
  const navigate = useNavigate()

  const ref = useRef<any>(null)
  const [isOpen, setOpen] = useState<boolean>(false)
  const [logoError, setLogoError] = useState<boolean>(false)
  const [logoutLoading, setLogoutLoading] = useState<boolean>(false)

  // Reset logo error when store data changes
  useEffect(() => {
    setLogoError(false)
  }, [data?.store?.businessLogo, data?.store?.id])

  const handleOpen = (): void => {
    setOpen(true)
  }

  const handleClose = (): void => {
    setOpen(false)
  }

  const handleLogout = async (): Promise<void> => {
    setLogoutLoading(true)
    try {
      await logout()
    } catch {
      setLogoutLoading(false)
      return // Only clear local data when logout API succeeds
    }
    // Clear analytics user properties on logout
    AnalyticsManager.shared.clearUserProperties()
    removeToken()
    removeSelectedStoreId()
    dispatchAction({ type: 'CLEAR_ALL_STORE_DATA' })
    setLogoutLoading(false)
    navigate('/login')
  }
  return (
    <>
      <Backdrop open={logoutLoading} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      {data?.store && (
        <UserBoxButton color="secondary" ref={ref} onClick={handleOpen}>
          {data?.store?.businessLogo && !logoError ? (
            <Box
              component="img"
              src={data.store.businessLogo}
              alt={data?.store?.name || 'Store Logo'}
              sx={{
                borderRadius: '50%',
                width: 44,
                height: 44,
                objectFit: 'contain',
                backgroundColor: '#f5f5f5',
              }}
              onError={() => {
                setLogoError(true)
              }}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 25,
                width: 44,
                height: 44,
                backgroundColor: '#314367',
              }}
            >
              <Typography variant="h2" sx={{ color: 'white' }}>
                {' '}
                {data?.store?.name ? Array.from(data.store.name)[0] : 'M'}{' '}
              </Typography>
            </Box>
          )}
          <Hidden mdDown>
            <UserBoxText>
              <UserBoxLabel variant="body1">
                <Typography variant="h5" noWrap sx={{ maxWidth: 250 }}>
                  {data?.store?.name || 'My Company (Not Set)'}
                </Typography>
              </UserBoxLabel>
              <UserBoxDescription variant="body2">
                <Typography noWrap sx={{ maxWidth: 250 }}>
                  {data?.store?.address}
                </Typography>
              </UserBoxDescription>
            </UserBoxText>
          </Hidden>
          <Hidden smDown>
            <ExpandMoreTwoToneIcon sx={{ ml: 1 }} />
          </Hidden>
        </UserBoxButton>
      )}
      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuUserBox sx={{ minWidth: 210 }} display="flex">
          <Avatar variant="rounded" alt={data?.store?.name} src={''} />
          <UserBoxText>
            <UserBoxLabel variant="body1">{data?.store?.name || 'My Company (Not Set)'}</UserBoxLabel>
            <UserBoxDescription variant="body2">
              <Typography noWrap maxWidth={300}>
                {data?.store?.address}
              </Typography>
            </UserBoxDescription>
          </UserBoxText>
        </MenuUserBox>
        <Divider sx={{ mb: 0 }} />
        <List sx={{ p: 1 }} component="nav">
          <ListItem
            button
            to={ApplicationLinks.profileDetailLink()}
            component={NavLink}
          >
            <AccountBoxTwoToneIcon fontSize="small" />
            <ListItemText primary="My Profile" />
          </ListItem>
          
          <ListItem
            button
            to={ApplicationLinks.profileSettingsLink()}
            component={NavLink}
          >
            <AccountTreeTwoToneIcon fontSize="small" />
            <ListItemText primary="Settings" />
          </ListItem>
        </List>
        <Divider />
        <Box sx={{ m: 1 }}>
          <Button color="primary" fullWidth onClick={handleLogout} disabled={logoutLoading}>
            {logoutLoading ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : <LockOpenTwoToneIcon sx={{ mr: 1 }} />}
            Sign out
          </Button>
        </Box>
      </Popover>
    </>
  )
}

export default HeaderUserbox
